/* eslint-disable confluence-feature-gating/no-preconditioning */
'use no memo';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css, styled, keyframes } from '@compiled/react';
import memoize from 'memoize-one';
import { useIntl } from 'react-intl';
import type { MutationUpdaterFn } from 'apollo-client';

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Inline from '@atlaskit/primitives/inline';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';

import { expValEquals } from '@confluence/feature-experiments';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { CommentRenderer } from '@confluence/comment/entry-points/CommentRenderer.ts';
import type {
	CommentInlineResolveProperties,
	ReactionsSummary,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type {
	SitePermissionType,
	InlineCommentQueryInlineResolveProperties,
} from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { fg } from '@confluence/feature-gating';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useContentType } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type {
	ActiveCommentInlineResolveProperties,
	FooterResolvedProperties,
} from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import { getUnreadCommentsIntersectionObserver } from '@confluence/unread-comments/entry-points/UnreadCommentsIntersectionObserver.ts';
import { useViewedComments } from '@confluence/unread-comments/entry-points/useViewedComments.ts';
import {
	isValidUnreadComment,
	buildValidationObjectFromProps,
} from '@confluence/unread-comments/entry-points/unreadCommentsUtils.ts';

import type {
	CommentPermissions,
	InlineCommentsMode,
	CommentAction,
} from './inlineCommentsTypes.ts';
import { i18n } from './inlineCommentsi18n.ts';
import { CommentActions } from './CommentActions.tsx';
import { DanglingContent } from './DanglingContent.tsx';
import { CommentAuthor } from './CommentAuthor.tsx';
import { WithCurvedBranchingStyle } from './WithCurvedBranchingStyle.tsx';
import { useCommentActions } from './hooks/useCommentActions.tsx';
import { useResizeObserver } from './hooks/useResizeObserver.ts';
import { useCommentActionsVisibility } from './hooks/useCommentActionsVisibility.ts';

type CommentContainerProps = {
	isFocused?: boolean;
	isRemoving?: boolean;
	mode: InlineCommentsMode;
	isCommentsPanel?: boolean;
	isFabricPage?: boolean;
	isCommentsTuneupActive: boolean;
};

const fadeOut = keyframes({
	from: { opacity: 1 },
	to: { opacity: 0.5 },
});

const unreadIconStyle = css({ paddingTop: token('space.050') });

const unreadStyleInCommentsPanel = css({
	position: 'relative',
	top: token('space.025'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentContainer = styled.div<CommentContainerProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	`
  display: flex;
  flex-direction: column;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
	padding: ${(props: CommentContainerProps) => {
		const horizontalSpacing = props.isCommentsTuneupActive
			? token('space.150')
			: token('space.200');

		const padding =
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			props.isFabricPage && fg('confluence_frontend_content_wrapper')
				? `${token('space.150')} ${horizontalSpacing} ${token('space.0')} ${horizontalSpacing}`
				: `${token('space.150')} ${token('space.200')}`;

		const contextDependentPadding = props.isCommentsPanel === true ? 'inherit' : padding;

		return props.mode === 'view-all' ? token('space.400') : contextDependentPadding;
	}};
  border-left: 0;
  border-top: 0;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  ${(props: CommentContainerProps) => Boolean(props.isRemoving) && `animation: ${fadeOut};`}
  animation-timing-function: linear;
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  cursor: ${(props: CommentContainerProps) => (props.isRemoving ? 'not-allowed' : 'inherit')};
  outline: none;

${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766 */ ''}
  background: ${(props: CommentContainerProps) =>
		props.isFocused && !props.isCommentsPanel
			? `${token('color.background.selected')}`
			: undefined};

  blockquote {
    color: ${token('color.text.subtlest')};
  }

  &:last-of-type {
    border-bottom: 0;
  }
`,
);

const standaloneStyle = css({
	marginLeft: token('space.150'),
});

const standaloneCommentWithoutRepliesStyle = css({
	paddingLeft: token('space.300'),
});

const withLeftBorderStyle = css({
	borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: token('space.100'),
});

const withLeftBorderStyleUpdated = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingBottom: token('space.100'),
});

const withLeftBorderStyleV2 = css({
	borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
	paddingLeft: token('space.250'),
	paddingBottom: token('space.0'),
});

const withoutLeftBorderStyle = css({
	paddingBottom: token('space.100'),
});

const commentBodyStyles = css({
	marginTop: token('space.150'),
	marginLeft: token('space.200'),
	paddingLeft: token('space.300'),
	paddingRight: token('space.050'),
});

const commentBodyStylesV2 = css({
	marginTop: token('space.100'),
	marginLeft: token('space.150'),
});

const commentReplyStyles = css({
	marginLeft: token('space.0'),
	paddingLeft: token('space.0'),
});

const commentBodyReplyStyles = css({
	marginTop: token('space.100'),
});

const commentBodyNestedReplyStyle = css({
	marginLeft: token('space.100'),
});

const warningMessageStyle = xcss({
	display: 'flex',
	fontStyle: 'italic',
	padding: 'space.150',
});

const xcssTranslucentStyle = xcss({
	opacity: '50%',
});

const cssTranslucentStyle = css({
	opacity: '50%',
});

const replyCommentBodyStyle = css({
	paddingLeft: token('space.100'),
});

const annotatedTextBodyStyle = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	padding: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

const danglingAnnotatedTextBodyStyle = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.small',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	paddingLeft: 'space.200',
	paddingRight: 'space.200',
	width: '100%',
	backgroundColor: 'elevation.surface',
	marginBottom: 'space.150',
});

const annotatedTextBodyWithPressedStyle = xcss({
	background: token('elevation.surface.pressed'),
});

const dotBaseStyles = xcss({
	height: token('space.100'),
	width: token('space.100'),
	backgroundColor: 'color.background.brand.bold',
	borderRadius: 'radius.full',
	margin: 'space.050',
	transition: 'opacity 300ms ease-in-out',
});

const dotVisibleStyles = xcss({
	opacity: 1,
});

const dotHiddenStyles = xcss({
	opacity: 0,
});

const replyBranchStyles = css({
	position: 'relative',
	// for the curvy border
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '-24px',
		top: '16px',
		height: `var(--replyBranchHeight)`,
		bottom: '30px',
		borderLeft: `1.5px solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		paddingLeft: token('space.200'),
	},
});

const replyBranchStylesUpdated = css({
	position: 'relative',
	// for the curvy border
	'&::before': {
		content: "''",
		position: 'absolute',
		left: '-24px',
		top: '16px',
		height: `var(--replyBranchHeight)`,
		bottom: '30px',
		borderLeft: `${token('border.width')} solid ${token('color.background.accent.gray.subtlest.hovered')}`,
		paddingLeft: token('space.200'),
	},
});

const replyBranchStylesV2 = css({
	'&::before': {
		left: '-16px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RelativeContainer = styled.div({
	position: 'relative',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginTop: '-1px', // Needs to offset height to prevent jumping when we switch to edit mode
});

const footerCommentAuthorStyles = xcss({
	paddingTop: 'space.100',
});

const replyCommentAuthorStyles = xcss({
	paddingLeft: 'space.050',
});

type CommentBodyProps = {
	pageId: string;
	pageType: string;
	avatarUrl?: string;
	commentId: string;
	content: string;
	date: string;
	displayName?: string | null;
	permissions: CommentPermissions;
	dateUrl?: string;
	createdAt?: string;
	createdAtNonLocalized?: string;
	versionByDisplayName?: string | null;
	versionDate: string;
	userId?: string | null;
	deleteComment?: () => void;
	editComment?: () => void;
	resolveComment?: () => void;
	isReply: boolean;
	isEdited: boolean;
	parentCommentId?: string | null;
	isFocused?: boolean;
	shouldScrollIntoView?: boolean;
	isRemoving?: boolean;
	numReplies?: number;
	isFabricPage?: boolean;
	isCommentActive?: boolean;
	isCurrentUserAnonymous?: boolean;
	mode: InlineCommentsMode;
	onRendered?: () => void;
	onClose?: () => void;
	shouldAutofocus?: boolean;
	onAutoFocused?: () => void;
	permissionType?: SitePermissionType;
	maxHeight?: number;
	fadeOutHeight?: number;
	onComplete?: () => void;
	supportedActions?: CommentAction[];
	isUnread?: boolean;
	isCommentsPanel?: boolean;
	isHovered?: boolean;
	isResolvedByAnotherUser?: boolean;
	resolveProperties?:
		| CommentInlineResolveProperties
		| FooterResolvedProperties
		| ActiveCommentInlineResolveProperties
		| InlineCommentQueryInlineResolveProperties;
	annotationId?: string;
	annotatedText?: string | null;
	hideAiSuggestEdit?: boolean;
	reopenComment?: () => void;
	replyToComment?: (commentId: string) => void;
	inheritedReactionsData?: ReactionsSummary | null;
	reactionsCacheUpdateFn?: (
		emojiId: string,
		actionType: 'add' | 'delete',
		contentId: string,
		containerId?: string,
	) => MutationUpdaterFn<any> | void;
	isDeletedByAnotherUser?: boolean;
	currentlySelectedThreadKey?: string;
	setCurrentlySelectedThreadKey?: (markerRef: string) => void;
	isAnnotatedTextPressed?: boolean;
	setAnnotatedTextPressed?: (state: boolean) => void;
	commentType: CommentType;
	adjustForNestedReplies?: boolean;
	hideBranchingStyle?: boolean;
	canAddComments?: boolean;
	isThreadHovered?: boolean;
	isParentCommentOpen?: boolean;
	isStandalonehovered?: boolean;
	isFooterComment?: boolean;
	adjustForFooterNestedReplies?: boolean;
	isStandaloneComment?: boolean;
	isOffline?: boolean;
	showRepliesWithCurvedBorders?: boolean;
	replyHasNestedObjects?: boolean;
};

// Parse content to JSON or Fail view inline experience and return null
const parseContent = memoize((content, experienceTracker) => {
	if (!content?.length) return null;

	try {
		return JSON.parse(content);
	} catch (error) {
		experienceTracker.stopOnError({
			name: VIEW_INLINE_COMMENT_EXPERIENCE,
			error,
			attributes: {
				stopPoint: 'inline-comments-common/CommentBody',
			},
		});
		return null;
	}
});

const STABLE_SUPPORTED_ACTIONS_DEFAULT: CommentAction[] = ['edit', 'delete', 'resolve'];

export const CommentBody = ({
	pageId,
	pageType,
	avatarUrl,
	commentId,
	content,
	date,
	createdAt,
	createdAtNonLocalized,
	versionByDisplayName,
	versionDate,
	displayName,
	permissions,
	dateUrl,
	userId,
	deleteComment,
	editComment,
	resolveComment,
	isReply,
	isEdited,
	parentCommentId,
	isFocused,
	shouldScrollIntoView: _shouldScrollIntoView,
	isRemoving,
	numReplies,
	isFabricPage,
	isCommentActive,
	isCurrentUserAnonymous,
	mode,
	onRendered,
	onClose,
	shouldAutofocus,
	onAutoFocused,
	permissionType,
	maxHeight,
	fadeOutHeight,
	onComplete,
	supportedActions = STABLE_SUPPORTED_ACTIONS_DEFAULT,
	isUnread = false,
	isCommentsPanel = false,
	isHovered,
	isResolvedByAnotherUser = false,
	resolveProperties,
	annotationId,
	annotatedText,
	hideAiSuggestEdit = false,
	reopenComment,
	replyToComment,
	inheritedReactionsData,
	reactionsCacheUpdateFn,
	isDeletedByAnotherUser,
	currentlySelectedThreadKey,
	setCurrentlySelectedThreadKey,
	isAnnotatedTextPressed,
	setAnnotatedTextPressed,
	commentType = CommentType.INLINE,
	adjustForNestedReplies = false,
	hideBranchingStyle = false,
	canAddComments = true,
	isThreadHovered,
	isParentCommentOpen,
	isStandalonehovered,
	isFooterComment = false,
	adjustForFooterNestedReplies = false,
	isStandaloneComment = false,
	isOffline = false,
	showRepliesWithCurvedBorders,
	replyHasNestedObjects = false,
}: CommentBodyProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [showingCopyLinkButton, setShowingCopyLink] = useState(false);
	const [hasFallbackContent, setHasFallbackContent] = useState(false);
	const [overflowPortal, setOverflowPortal] = useState<HTMLDivElement | undefined>(undefined);
	const [isHoveredLocal, setIsHoveredLocal] = useState(false);
	const isMouseOverContainerRef = useRef(false);

	const isSelected = currentlySelectedThreadKey === annotationId && !isStandalonehovered;
	const [contentType] = useContentType();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const [containerRef, replyBranchHeight] = useResizeObserver();

	// Track the currently observed element for cleanup
	const [observedElement, setObservedElement] = useState<HTMLElement | null>(null);

	const { getViewedCommentsCacheOnly } = useViewedComments({ pageId: pageId || '' });
	const { userId: currentUserId } = useSessionData();

	// Create a ref callback to set the ref and observe when the DOM element is available
	const handleUnreadCommentRef = useCallback(
		(node: HTMLDivElement | null) => {
			// isUnread comes from unreadCommentIDSet and doesn't contain dangling and resolved, we're only using that for the unread indicators
			// for observing unread comments, we'll rely on the viewedComments set as the source of truth
			const viewedCommentsResult = getViewedCommentsCacheOnly();
			const viewedComments =
				viewedCommentsResult.success && viewedCommentsResult.data
					? viewedCommentsResult.data
					: new Set<string>();

			const commentForValidation = buildValidationObjectFromProps({
				commentId,
				userId,
				createdAt,
				commentType: commentType === CommentType.INLINE ? 'INLINE' : 'FOOTER',
				resolveProperties: { resolved: false }, // overriding resolveProperties since we still want to observe resolved comments for unread
			});

			const isValidUnread = isValidUnreadComment({
				comment: commentForValidation,
				currentUserId: currentUserId || '',
				viewedComments,
			});

			const shouldObserve = node && isValidUnread;
			if (shouldObserve) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.observeComment(node);
				setObservedElement(node);
			} else {
				// If we shouldn't observe this node, clear the observed element
				setObservedElement(null);
			}
		},
		[commentId, userId, createdAt, commentType, getViewedCommentsCacheOnly, currentUserId],
	);

	const { pageInfo } = usePageInfo();
	const isArchived = pageInfo?.status === 'archived';

	const { mainCommentActions, hoveredActionItems, isDropdownOpen } = useCommentActions({
		pageId,
		pageType,
		commentId,
		permissions,
		isReply,
		parentCommentId,
		deleteComment,
		editComment,
		resolveComment,
		restrictEdit: hasFallbackContent,
		restrictDelete: Boolean(numReplies),
		numReplies,
		mode,
		commentDateUrl: dateUrl,
		overflowMenuPortal: overflowPortal,
		supportedActions,
		isCommentsPanel,
		isHovered: expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
			? isHoveredLocal
			: (isHovered ?? false),
		annotationId,
		resolveProperties: resolveProperties as
			| CommentInlineResolveProperties
			| FooterResolvedProperties,
		reopenComment,
		replyToComment,
		inheritedReactionsData,
		reactionsCacheUpdateFn,
		isResolvedByAnotherUser,
		commentType,
		hideBranchingStyle,
		canAddComments,
		isDeletedByAnotherUser,
		isSelected,
		isParentCommentOpen,
		isFooterComment,
		isStandaloneComment,
		isOffline,
		isFabricPage,
		isArchived,
		authorName: displayName,
	});

	const isHoveredComment = expValEquals(
		'confluence_frontend_fix_comment_actions_visibility',
		'isEnabled',
		true,
	)
		? isHoveredLocal || isDropdownOpen
		: (isHovered ?? false);

	const { showMainActions, showLegacyMainActions } = useCommentActionsVisibility(isFabricPage);
	const adf = parseContent(content, experienceTracker);

	useEffect(() => {
		if (shouldAutofocus) {
			containerRef.current &&
				containerRef.current.focus({
					preventScroll: true,
				});
			onAutoFocused && onAutoFocused();
		}
	}, [containerRef, shouldAutofocus, onAutoFocused]);

	// clean up the observer when the component unmounts
	useEffect(() => {
		return () => {
			if (observedElement) {
				const observer = getUnreadCommentsIntersectionObserver();
				observer.unobserveComment(observedElement);
			}
		};
	}, [observedElement]);

	const onCommentContentFallback = useCallback(() => setHasFallbackContent(true), []);

	const handleRef = (portalDiv: HTMLDivElement) => {
		/* do a check to prevent from re-running setPopupPortal multiple times*/
		if (!overflowPortal && portalDiv) {
			setOverflowPortal(portalDiv);
		}
	};

	const handleMouseEventOnWrapper = (eventType: 'enter' | 'leave') => () => {
		setShowingCopyLink(eventType === 'enter');
		isMouseOverContainerRef.current = eventType === 'enter';
		if (expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)) {
			setIsHoveredLocal(eventType === 'enter');
		}
	};

	const commentRemovedByAnotherUser = isDeletedByAnotherUser || isResolvedByAnotherUser;
	const shouldShowOverflowMenu = supportedActions.length > 0;

	const showDefaultReplyStyle = showRepliesWithCurvedBorders ?? (isCommentsPanel && isReply);

	const getHighlightBackgroundColor = () => {
		if (isAnnotatedTextPressed) {
			return token('color.background.accent.yellow.subtle.hovered');
		}
		if (isSelected) {
			return token('color.background.accent.yellow.subtle');
		}
		if (isThreadHovered) {
			return token('color.background.accent.yellow.subtlest.hovered');
		}
		return 'inherit';
	};

	const handleMouseUp = () => {
		if (annotationId && setCurrentlySelectedThreadKey) {
			setAnnotatedTextPressed && setAnnotatedTextPressed(false);
		}
	};

	const shouldShowCommentContentContext = annotatedText && isCommentsPanel && !isReply;

	const showContentUnavailableForDanglingComment =
		(resolveProperties as CommentInlineResolveProperties)?.isDangling &&
		fg('confluence_frontend_content_wrapper');

	return (
		<CommentContainer
			ref={containerRef}
			data-comment-id={commentId}
			data-testid={commentId}
			isFocused={isFocused}
			isRemoving={isRemoving}
			mode={mode}
			isCommentsPanel={isCommentsPanel}
			role="article"
			tabIndex={-1}
			onMouseEnter={handleMouseEventOnWrapper('enter')}
			onMouseLeave={handleMouseEventOnWrapper('leave')}
			onFocusCapture={
				expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
					? () => setIsHoveredLocal(true)
					: undefined
			}
			onBlurCapture={
				expValEquals('confluence_frontend_fix_comment_actions_visibility', 'isEnabled', true)
					? (e: React.FocusEvent) => {
							const next = e.relatedTarget as Node | null;
							const container = e.currentTarget;
							if (next && container.contains(next)) return;
							// Only clear hover when focus leaves if the mouse has left too – mouse takes precedence
							if (!isMouseOverContainerRef.current) {
								setIsHoveredLocal(false);
							}
						}
					: undefined
			}
			css={{ position: 'relative ' }}
			isFabricPage={isFabricPage}
			isCommentsTuneupActive={expValEquals('confluence_comments_tuneup', 'isEnabled', true)}
		>
			<div ref={handleUnreadCommentRef} data-comment-id={commentId}>
				{mode !== 'view' && (
					<RelativeContainer>
						<div data-testid="overflow-container" ref={handleRef} />
					</RelativeContainer>
				)}
				<div
					css={[
						isReply && isCommentsPanel && !hideBranchingStyle && replyBranchStyles,
						// updated border
						isReply &&
							isCommentsPanel &&
							!hideBranchingStyle &&
							fg('confluence_comment_border_migration') &&
							replyBranchStylesUpdated,
						isReply &&
							isCommentsPanel &&
							!hideBranchingStyle &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							replyBranchStylesV2,
					]} // We need to adjust the size of the border if we have a nested reply scroller below this
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={
						!hideBranchingStyle
							? ({
									'--replyBranchHeight': `${replyBranchHeight + (adjustForNestedReplies ? 53 : 0)}px`,
								} as React.CSSProperties)
							: {}
					}
				>
					<WithCurvedBranchingStyle
						showDefaultStyles={showDefaultReplyStyle}
						customStyles={{
							...(isCommentsPanel
								? {
										height: fg('confluence_frontend_content_wrapper') ? '28px' : '20px',
									}
								: {}), // Have to adjust for padding on author (vs reply thread parent) with flag on
							...(isFooterComment ? { height: '25px' } : {}), // Accounts for additional padding added to the author
							...(adjustForFooterNestedReplies ? { left: '-25px' } : {}), // Accounts for the left border of the parent comment
							...(expValEquals('confluence_comments_tuneup', 'isEnabled', true)
								? { left: '-16px', height: '24px' }
								: {}),
						}}
					>
						<Box
							xcss={[
								isFooterComment ||
								(isCommentsPanel && isReply && fg('confluence_frontend_content_wrapper'))
									? footerCommentAuthorStyles
									: undefined,
								isCommentsPanel &&
									isReply &&
									expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
									replyCommentAuthorStyles,
							]}
						>
							<Inline
								testId="comment-author-box"
								spread="space-between"
								alignBlock={
									expValEquals('confluence_comments_tuneup', 'isEnabled', true)
										? 'center'
										: undefined
								}
							>
								<CommentAuthor
									commentMode="view"
									userId={userId}
									date={date}
									createdAtNonLocalized={createdAtNonLocalized}
									versionByDisplayName={versionByDisplayName}
									versionDate={versionDate}
									displayName={displayName}
									avatarUrl={avatarUrl}
									commentDateUrl={dateUrl}
									commentId={isCurrentUserAnonymous ? commentId : null}
									permissionType={permissionType}
									isInactiveComment={Boolean(mode === 'view-all' && !isCommentActive)}
									showCopyLink={showingCopyLinkButton}
									resolveProperties={
										resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties
									}
									size={
										expValEquals('confluence_comments_tuneup', 'isEnabled', true)
											? 'small'
											: 'medium'
									}
									isCommentsPanel={isCommentsPanel}
									isGeneralComments={commentType === CommentType.GENERAL}
									isReply={isReply}
									isEdited={isEdited}
									isFooterComment={isFooterComment}
									isStandaloneComment={isStandaloneComment}
								/>
								<div
									css={[
										!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
											unreadIconStyle,
										isCommentsPanel &&
											!expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
											unreadStyleInCommentsPanel,
									]}
									data-testid={`unread-comment-indicator-${commentId}`}
									// Conditionally rendering this element can cause layout shift when the comment is hovered
									// and the layout is recalculated, so we're using css to hide it instead
									style={{ visibility: isHoveredComment ? 'hidden' : 'visible' }}
									aria-hidden={isHoveredComment || !isUnread}
								>
									<Box xcss={[dotBaseStyles, isUnread ? dotVisibleStyles : dotHiddenStyles]} />
								</div>
							</Inline>
						</Box>
					</WithCurvedBranchingStyle>
				</div>
				{shouldShowOverflowMenu && fg('confluence_frontend_content_wrapper') && hoveredActionItems}
				<div
					css={[
						isCommentsPanel && commentBodyStyles,
						isCommentsPanel &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							commentBodyStylesV2,
						isCommentsPanel && isReply && commentReplyStyles,
						isCommentsPanel &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							withoutLeftBorderStyle,
						isCommentsPanel &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							withLeftBorderStyle,
						isCommentsPanel &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							fg('confluence_comment_border_migration') &&
							withLeftBorderStyleUpdated,
						isStandaloneComment &&
							!isReply &&
							!hideBranchingStyle &&
							(numReplies !== 0 || canAddComments) &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							withLeftBorderStyleV2,
						isFooterComment && isReply && commentBodyReplyStyles,
						isFooterComment && isReply && replyHasNestedObjects && commentBodyNestedReplyStyle,
						isStandaloneComment && isReply && commentBodyReplyStyles,
						isCommentsPanel &&
							isReply &&
							fg('confluence_frontend_content_wrapper') &&
							commentBodyReplyStyles,
						!isCommentsPanel &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							standaloneStyle,
						!isCommentsPanel &&
							numReplies === 0 &&
							expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
							standaloneCommentWithoutRepliesStyle,
					]}
				>
					{shouldShowCommentContentContext && (
						<>
							{contentType !== 'database' && contentType !== 'slide' && (
								<Inline
									space="space.150"
									alignBlock="center"
									xcss={[
										isResolvedByAnotherUser &&
											!fg('confluence_frontend_content_wrapper') &&
											xcssTranslucentStyle,
									]}
									testId="highlight-text"
								>
									<Box
										data-testId="annotated-section-for-inlineComment"
										xcss={[
											showContentUnavailableForDanglingComment &&
											fg('confluence_frontend_new_dangling_comments_ux')
												? danglingAnnotatedTextBodyStyle
												: annotatedTextBodyStyle,
											!isResolvedByAnotherUser &&
												isAnnotatedTextPressed &&
												!showContentUnavailableForDanglingComment &&
												annotatedTextBodyWithPressedStyle,
										]}
										onMouseDown={() => {
											if (annotationId) {
												setAnnotatedTextPressed && setAnnotatedTextPressed(true);
											}
										}}
										onMouseUp={handleMouseUp}
									>
										{showContentUnavailableForDanglingComment ? (
											<DanglingContent annotatedText={annotatedText} />
										) : (
											annotatedText && (
												<Text size="small" maxLines={3}>
													<span
														style={{
															background: getHighlightBackgroundColor(),
															borderBottom:
																isAnnotatedTextPressed || isSelected || isThreadHovered
																	? `2px solid ${token('color.icon.accent.yellow')}`
																	: '',
														}}
													>
														{annotatedText}
													</span>
												</Text>
											)
										)}
									</Box>
								</Inline>
							)}
						</>
					)}
					{adf && (
						<>
							<div
								css={[
									isCommentsPanel &&
										commentRemovedByAnotherUser &&
										!fg('confluence_frontend_content_wrapper') &&
										cssTranslucentStyle,
									isCommentsPanel &&
										isReply &&
										expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
										replyCommentBodyStyle,
								]}
							>
								<CommentRenderer
									commentId={commentId}
									adf={adf}
									onContentFallback={onCommentContentFallback}
									isTruncatedContent={mode === 'view-all' && !isCommentActive}
									onRendered={onRendered}
									onComplete={onComplete}
									maxHeight={maxHeight}
									fadeOutHeight={fadeOutHeight}
									isInlineComment
									isCommentsPanel={isCommentsPanel}
									isEdited={isEdited}
									versionByDisplayName={versionByDisplayName}
									versionDate={versionDate}
									commentType={commentType}
									annotationId={annotationId}
									annotatedText={annotatedText ?? undefined}
									hideAiSuggestEdit={hideAiSuggestEdit}
									isReply={isReply}
								/>
							</div>
							{isDeletedByAnotherUser && (
								<Box xcss={warningMessageStyle}>
									<Text weight="bold">{formatMessage(i18n.deletedCommentActionLabel)}</Text>
								</Box>
							)}
						</>
					)}
				</div>
				{shouldShowOverflowMenu && showMainActions && mainCommentActions}
				{/* TODO: delete when cleaning up 'confluence_frontend_content_wrapper' FG */}
				{shouldShowOverflowMenu && showLegacyMainActions && (
					<CommentActions
						pageId={pageId}
						pageType={pageType}
						commentId={commentId}
						permissions={permissions}
						isReply={isReply}
						parentCommentId={parentCommentId}
						deleteComment={deleteComment}
						editComment={editComment}
						resolveComment={resolveComment}
						restrictEdit={hasFallbackContent}
						restrictDelete={Boolean(numReplies)}
						numReplies={numReplies}
						isCommentActive={isCommentActive}
						mode={mode}
						commentDateUrl={dateUrl}
						onClose={onClose}
						overflowMenuPortal={overflowPortal}
						supportedActions={supportedActions}
						isCommentsPanel={isCommentsPanel}
						isHovered={isHoveredComment}
						annotationId={annotationId}
						resolveProperties={
							resolveProperties as CommentInlineResolveProperties | FooterResolvedProperties
						}
						reopenComment={reopenComment}
						replyToComment={replyToComment}
						isUnread={isUnread}
						inheritedReactionsData={inheritedReactionsData}
						reactionsCacheUpdateFn={reactionsCacheUpdateFn}
						isResolvedByAnotherUser={isResolvedByAnotherUser}
						commentType={commentType}
						hideBranchingStyle={hideBranchingStyle}
						canAddComments={canAddComments}
						isDeletedByAnotherUser={isDeletedByAnotherUser}
						isSelected={isSelected}
						isParentCommentOpen={isParentCommentOpen}
						isFooterComment={isFooterComment}
						isStandaloneComment={isStandaloneComment}
						isOffline={isOffline}
						isFabricPage={isFabricPage}
						isArchived={isArchived}
						authorName={displayName}
					/>
				)}
			</div>
		</CommentContainer>
	);
};
